import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, MapPin, CalendarDays, CheckCircle2, CreditCard, Sparkles, Users } from "lucide-react";

export default function TournamentInvite() {
  const { token } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [myTeams, setMyTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [roster, setRoster] = useState([]);
  const [picked, setPicked] = useState({});
  const [linking, setLinking] = useState(false);

  const isCoach = user && (user.role === "coach" || user.role === "admin") && user.club_id;

  const load = () => api.get(`/interclub/invite/${token}`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (isCoach) api.get("/teams").then((r) => setMyTeams(r.data || [])).catch(() => {}); }, [isCoach]);
  useEffect(() => {
    if (teamId) api.get(`/players?team_id=${teamId}`).then((r) => { setRoster(r.data || []); setPicked(Object.fromEntries((r.data || []).map((p) => [p.id, true]))); }).catch(() => {});
    else { setRoster([]); setPicked({}); }
  }, [teamId]);

  const accept = async () => {
    setBusy(true);
    try { await api.post(`/interclub/invite/${token}/accept`); toast.success("Participation confirmed!"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pay = async () => {
    setBusy(true);
    try { const r = await api.post(`/interclub/invite/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  const linkTeam = async () => {
    if (!teamId) return toast.error("Choose one of your teams");
    const player_ids = Object.keys(picked).filter((k) => picked[k]);
    setLinking(true);
    try { const r = await api.post(`/interclub/invite/${token}/link`, { team_id: teamId, player_ids }); toast.success(`Team entered with ${r.data.squad_size} players`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setLinking(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background grain px-4"><div className="text-center"><h1 className="font-heading font-bold text-2xl">Invitation not found</h1><p className="text-muted-foreground mt-2">{err}</p><Link to="/"><Button className="mt-6" variant="outline">Go home</Button></Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const { team, tournament: t, host } = data;
  const color = host?.primary_color || "#007AFF";
  const currency = host?.currency || "USD";
  const paid = team.payment_status === "paid" || team.payment_status === "waived";
  const pickedCount = Object.values(picked).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background grain">
      <div className="h-40 w-full flex items-center px-6" style={{ background: `linear-gradient(135deg, ${color}, ${host?.secondary_color || color})` }}>
        <div className="max-w-2xl mx-auto w-full flex items-center gap-4">
          {host?.logo ? <img src={host.logo} alt="" className="h-14 w-14 rounded-xl object-cover border-2 border-white/30" /> : <div className="h-14 w-14 rounded-xl bg-white/20" />}
          <div><p className="text-white/80 text-xs uppercase tracking-wide">Tournament invitation</p><h1 className="text-white font-heading font-bold text-2xl" data-testid="invite-host-name">{host?.name}</h1></div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 -mt-8 pb-16">
        <div className="rounded-2xl border border-border bg-card p-7 space-y-5 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}><Trophy size={22} /></div>
            <div>
              <h2 className="font-heading font-bold text-2xl" data-testid="invite-tournament-name">{t?.name}</h2>
              <p className="text-muted-foreground text-sm">Your team <b className="text-foreground">{team.name}</b> is invited to compete.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {(t?.start_date) && <div className="flex items-center gap-2"><CalendarDays size={16} className="text-muted-foreground" />{t.start_date}{t.end_date ? ` – ${t.end_date}` : ""}</div>}
            {t?.location && <div className="flex items-center gap-2"><MapPin size={16} className="text-muted-foreground" />{t.location}</div>}
          </div>

          {t?.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t.description}</p>}

          {t?.entry_fee > 0 && (
            <div className="rounded-xl border border-border bg-muted/40 p-4 flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Entry fee per team</p><p className="font-heading font-bold text-xl">{currency} {(+t.entry_fee).toFixed(2)}</p></div>
              {paid ? <span className="inline-flex items-center gap-1.5 text-emerald-500 font-semibold text-sm" data-testid="invite-paid-badge"><CheckCircle2 size={18} /> {team.payment_status === "waived" ? "Fee waived" : "Paid"}</span>
                : <Button onClick={pay} disabled={busy} className="text-white border-0" style={{ background: color }} data-testid="invite-pay-btn"><CreditCard size={16} className="mr-2" /> Pay entry fee</Button>}
            </div>
          )}

          {t?.terms && (
            <div className="rounded-xl border border-border p-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Terms &amp; conditions</p>
              <p className="text-sm whitespace-pre-wrap">{t.terms}</p>
            </div>
          )}

          {/* Squad already selected */}
          {team.squad?.length > 0 && (
            <div className="rounded-xl border border-border p-4" data-testid="invite-squad-list">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Users size={16} /> Squad for {team.linked_team_name || team.name} ({team.squad.length})</p>
              <div className="flex flex-wrap gap-2">
                {team.squad.map((p) => <span key={p.player_id} className="text-xs bg-muted rounded-full px-2.5 py-1">{p.jersey_number ? `#${p.jersey_number} ` : ""}{p.name}</span>)}
              </div>
            </div>
          )}

          {/* Squad picker for logged-in SphereHyx coaches */}
          {isCoach && !team.linked_team_id && (
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4" data-testid="invite-squad-picker">
              <p className="text-sm font-semibold flex items-center gap-2"><Users size={16} /> Enter one of your existing teams</p>
              <p className="text-xs text-muted-foreground mb-3">No duplicate rosters — pick a team you already manage and choose the squad.</p>
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger className="h-10" data-testid="squad-team-select"><SelectValue placeholder="Choose a team" /></SelectTrigger>
                <SelectContent>{myTeams.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}</SelectContent>
              </Select>
              {roster.length > 0 && (
                <div className="mt-3 max-h-52 overflow-y-auto grid sm:grid-cols-2 gap-1.5">
                  {roster.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-lg px-2 py-1 hover:bg-accent" data-testid={`squad-player-${p.id}`}>
                      <input type="checkbox" checked={!!picked[p.id]} onChange={() => setPicked((s) => ({ ...s, [p.id]: !s[p.id] }))} className="h-4 w-4 accent-primary" />
                      {p.jersey_number ? <span className="text-muted-foreground">#{p.jersey_number}</span> : null}{p.name}
                    </label>
                  ))}
                </div>
              )}
              <Button onClick={linkTeam} disabled={linking || !teamId} className="w-full mt-3 text-white border-0 h-10" style={{ background: color }} data-testid="squad-submit-btn">{linking ? "Entering..." : `Enter team${teamId ? ` with ${pickedCount} players` : ""}`}</Button>
            </div>
          )}

          <div className="pt-1">
            {team.accepted || team.squad?.length > 0 ? (
              <div className="flex items-center gap-2 text-emerald-500 font-semibold" data-testid="invite-accepted"><CheckCircle2 size={18} /> Participation confirmed</div>
            ) : (
              <Button onClick={accept} disabled={busy} className="w-full text-white border-0 h-11" style={{ background: color }} data-testid="invite-accept-btn">Confirm participation</Button>
            )}
          </div>

          {!isCoach && (
            <div className="rounded-xl border border-dashed border-border p-4 text-center">
              <Sparkles size={18} className="mx-auto text-primary mb-1" />
              <p className="text-sm font-medium">Already on SphereHyx?</p>
              <p className="text-xs text-muted-foreground mt-0.5">Log in to enter your existing team &amp; squad — or create a free club.</p>
              <div className="flex gap-2 justify-center mt-3">
                <Link to={`/login?next=/tournament-invite/${token}`}><Button variant="outline" size="sm" data-testid="invite-login-btn">Log in</Button></Link>
                <Link to="/register"><Button variant="outline" size="sm" data-testid="invite-create-club-btn">Create free club</Button></Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
